import { ImagePlus, Trash01 } from "@untitled-ui/icons-react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

import DropZoneServer from "@/components/helpers/DropZoneServer";
import Loading from "@/components/loadings/Loading";
import type { Media } from "@/types";

type GroupImageUploadProps = {
  imageUrl?: string | null;
  imageMedia?: Media | null;
  isUploading?: boolean;
  onUpload: (media: Media) => void;
  onDelete: () => void;
};

export default function GroupImageUpload({
  imageUrl,
  imageMedia,
  isUploading,
  onUpload,
  onDelete,
}: GroupImageUploadProps) {
  const { t } = useTranslation("messenger.user");

  const preview = imageMedia?.preview_url ?? imageUrl ?? undefined;

  const centerClass =
    "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2";

  return (
    <DropZoneServer
      type="image"
      accept={["image/*"]}
      hasBorder={false}
      media={
        imageMedia
          ? {
              file_name: imageMedia.file_name,
              preview_url: imageMedia.preview_url,
              size: imageMedia.size,
            }
          : preview
            ? {
                file_name: t("Group Image"),
                preview_url: preview,
                size: 0,
              }
            : null
      }
      onUpload={onUpload}
      onDeleted={onDelete}
    >
      <div className="flex shrink-0 justify-center">
        <div
          className={classNames(
            "group relative size-12",
            !preview && !isUploading && "cursor-pointer",
          )}
        >
          <div
            className={classNames(
              "flex size-full items-center justify-center overflow-hidden rounded-full bg-gray-100 transition-all",
              preview && "group-hover:blur-xs",
            )}
          >
            {preview ? (
              <img
                src={preview}
                alt={t("Group Image")}
                className="size-full object-cover"
              />
            ) : (
              <ImagePlus className="size-7 text-gray-500" />
            )}
          </div>
          {isUploading ? (
            <Loading className={centerClass} />
          ) : (
            preview && (
              <button
                type="button"
                className={classNames(
                  centerClass,
                  "hidden size-8 cursor-pointer group-hover:block",
                )}
                onClick={(event) => {
                  event.stopPropagation();
                  onDelete();
                }}
              >
                <Trash01 className="text-error-600 size-8" />
              </button>
            )
          )}
        </div>
      </div>
    </DropZoneServer>
  );
}
